import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Clock3,
  Building2,
  Copy,
  ExternalLink,
  Download,
  ChevronDown,
  Upload,
  ShieldCheck,
  Clock,
} from "lucide-react";

export default function Verification() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [verificationInput, setVerificationInput] = useState(
    "a3f2c6e5d4b7e9f123c8a6d5be4f2b1c9a7d8e6f2a1b3c4d5e6f7a8b9c0d1e2f3"
  );
  const [fileType] = useState("Document");
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    cert: true,
    tsa: true,
    blockchain: true,
  });

  const fullHash =
    "a3f2c6e5d4b7e9f123c8a6d5be4f2b1c9a7d8e6f2a1b3c4d5e6f7a8b9c0d1e2f3";

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setVerificationInput(file.name);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard fallback
    }
  };

  const timelineEvents = [
    {
      time: "10:35:22 AM",
      title: "Request Received",
      description: "Verification request received for the timestamp.",
    },
    {
      time: "10:35:22 AM",
      title: "File Hash Computed",
      description: "SHA-256 hash computed for the file.",
      codeBlock: fullHash,
    },
    {
      time: "10:35:22 AM",
      title: "Timestamp Located",
      description: "Matching timestamp record found in database.",
      tag: "TS-2025-05-31-001",
    },
    {
      time: "10:35:22 AM",
      title: "Signature Verified",
      description: "Timestamp signature verified successfully.",
      tag: "ML-DSA (Dilithium3)",
    },
    {
      time: "10:35:22 AM",
      title: "Certificate Validated",
      description: "TSA certificate is valid and trusted.",
      tag: "Valid until May 31, 2026",
    },
    {
      time: "10:35:22 AM",
      title: "Trusted Time Confirmed",
      description: "Timestamp is within the trusted time and policy.",
    },
    {
      time: "10:35:22 AM",
      title: "Verification Successful",
      description: "The timestamp is valid and the file is intact.",
      success: true,
    },
  ];

  const verificationDetails = [
    { label: "Timestamp ID", value: "TS-2025-05-31-001", action: "copy", mono: true },
    { label: "File / Reference", value: "Service_Agreement.pdf", action: "copy" },
    { label: "SHA-256 Hash", value: fullHash, action: "copy", mono: true, wrap: true },
    { label: "Timestamp", value: "May 31, 2025 10:30:15 AM (UTC)" },
    { label: "Verification Time", value: "May 31, 2025 10:35:22 AM (UTC)" },
    { label: "TSA Authority", value: "I-PQSign TSA 01" },
    { label: "Certificate", value: "I-PQSign TSA 01 Certificate", action: "download" },
    { label: "Serial Number", value: "01A7F3C29B04E6F8", mono: true },
    { label: "Signature Algorithm", value: "ML-DSA (Dilithium3)" },
    { label: "Hash Algorithm", value: "SHA-256" },
    { label: "Policy OID", value: "1.3.6.1.4.1.99999.1.1.1", mono: true },
    { label: "Status", value: "Verified", action: "badge" },
    {
      label: "Verification Result",
      value: "The timestamp is cryptographically valid and the file is unchanged.",
    },
    {
      label: "IPFS CID",
      value: "QmX7e5g9J8W2ny42Ks9o8u7G3hST8u1p2L4m6N8o0PqR",
      action: "copy",
      mono: true,
    },
    { label: "Blockchain Anchor", value: "View on Blockchain", action: "external" },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800 -mx-6 -mt-6">
      {/* Header Navigation */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2.5">
          <nav className="flex gap-6">
            {['Overview', 'Timestamp Records', 'Verification', 'Time Sources', 'Authorities', 'Settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'Verification') return;
                  if (tab === 'Time Sources') navigate('/timestamps/TimeSource');
                  else if (tab === 'Timestamp Records') navigate('/timestamps/records');
                  else if (tab === 'Overview') navigate('/timestamps');
                  else if (tab === 'Authorities') navigate('/timestamps/authorities');
                  else if (tab === 'Settings') navigate('/timestamps/settings');
                  else navigate('/timestamps');
                }}
                className={`pb-1.5 text-xs font-medium transition-colors ${
                  tab === 'Verification'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <div className="mx-auto max-w-[1580px] space-y-3 px-6 pt-4">
        {/* Top Section: File Search Form & Verification Banner */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          {/* Left Input Card */}
          <div className="lg:col-span-9 bg-white rounded-lg border border-slate-200/90 px-3 py-2 shadow-xs flex flex-col gap-1.5">
            <label className="block text-xs font-bold text-slate-700">
              Enter file / reference / hash to verify
            </label>

              <div className="flex flex-col sm:flex-row items-center gap-2">
                <input
                  type="text"
                  value={verificationInput}
                  onChange={(e) => setVerificationInput(e.target.value)}
                  placeholder="Enter hash, filename, or reference..."
                  className="h-10 min-w-0 flex-1 w-full rounded-lg border border-slate-200 bg-white px-3.5 text-xs text-slate-700 outline-none transition focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 font-mono shadow-2xs"
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />

                <button
                  type="button"
                  onClick={handleBrowse}
                  className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 shrink-0"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Browse File
                </button>

                <button
                  type="button"
                  className="flex h-10 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-5 text-xs font-semibold text-white transition hover:bg-indigo-700 shrink-0"
                >
                  <Clock className="h-4 w-4" />
                  Verify
                </button>
              </div>

            {/* Bottom Metadata Line */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>
                <strong className="text-slate-700 font-semibold">Type:</strong> {fileType}
              </span>
              <span>
                <strong className="text-slate-700 font-semibold">File Name:</strong>{" "}
                {selectedFile?.name || "Service_Agreement.pdf"}
              </span>
              <span className="truncate max-w-md">
                <strong className="text-slate-700 font-semibold">SHA-256:</strong>{" "}
                <span className="font-mono text-slate-500">{fullHash}</span>
              </span>
            </div>
          </div>

          {/* Right Verification Successful Card */}
          <div className="lg:col-span-3 bg-[#f0fbf5] border border-emerald-200/90 rounded-lg px-3 py-2 shadow-xs flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <Check className="h-4 w-4 stroke-[2.5]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 leading-tight">
                  Verification Successful
                </h3>
                <p className="text-xs text-emerald-700 font-medium">
                  This timestamp is authentic and valid.
                </p>
              </div>
            </div>
            <div className="pt-1.5 border-t border-emerald-100">
              <p className="text-[11px] font-medium text-slate-500">Verified on</p>
              <p className="text-xs font-bold text-slate-900">
                May 31, 2025 10:35:22 AM (UTC)
              </p>
            </div>
          </div>
        </section>

        {/* 5 Status Cards Row */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <StatusCard
            icon={<ShieldCheck className="h-5 w-5 text-emerald-600" />}
            title="Timestamp Status"
            value="Valid"
            footer="The timestamp is valid and trusted."
            iconBg="bg-emerald-50"
          />
          <StatusCard
            icon={<Check className="h-5 w-5 text-emerald-600" />}
            title="Integrity"
            value="Intact"
            footer="The file has not been modified."
            iconBg="bg-emerald-50"
          />
          <StatusCard
            icon={<Clock3 className="h-5 w-5 text-indigo-600" />}
            title="Timestamp Time"
            value="May 31, 2025 10:30:15 AM (UTC)"
            footer="10 seconds ago"
            footerColor="text-indigo-600 font-medium"
            iconBg="bg-indigo-50"
          />
          <StatusCard
            icon={<Building2 className="h-5 w-5 text-indigo-600" />}
            title="Authority"
            value="I-PQSign TSA 01"
            footer="Trusted Authority"
            iconBg="bg-indigo-50"
          />
          <StatusCard
            icon={<ShieldCheck className="h-5 w-5 text-emerald-600" />}
            title="Certificate Status"
            value="Valid"
            footer="Certificate is valid and trusted."
            iconBg="bg-emerald-50"
          />
        </section>

        {/* Main Content Layout */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Left Column: Verification Details */}
          <div className="lg:col-span-4 bg-white rounded-lg border border-slate-200/90 p-4 shadow-xs flex flex-col justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2.5 mb-1">
                Verification Details
              </h2>
              <div className="divide-y divide-slate-100">
                {verificationDetails.map((item) => (
                  <VerificationRow
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    action={item.action}
                    mono={item.mono}
                    wrap={item.wrap}
                    onCopy={() => copyText(item.value)}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              className="mt-5 flex h-8 w-fit items-center gap-2 rounded-md bg-indigo-50 px-3.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 transition"
            >
              <Download className="h-3.5 w-3.5" />
              Download Verification Report
            </button>
          </div>

          {/* Middle Column: Verification Timeline */}
          <div className="lg:col-span-5 bg-white rounded-lg border border-slate-200/90 p-4 shadow-xs">
            <h2 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2.5 mb-3">
              Verification Timeline
            </h2>

            <div className="space-y-3">
              {timelineEvents.map((event, index) => (
                <TimelineEvent
                  key={event.title}
                  event={event}
                  isLast={index === timelineEvents.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Right Column: Stacked Cards */}
          <div className="lg:col-span-3 space-y-3">
            {/* Certificate Details */}
            <CollapsiblePanel
              title="Verification Details"
              expanded={expandedSections.cert}
              onToggle={() => toggleSection("cert")}
            >
              <DetailRow label="Certificate" value="I-PQSign TSA 01 Certificate" isLink />
              <DetailRow label="Issued To" value="I-PQSign TSA 01" />
              <DetailRow label="Issued By" value="I-PQSign Root CA" />
              <DetailRow label="Valid From" value="May 31, 2024" />
              <DetailRow label="Valid Until" value="May 31, 2026" />
              <DetailRow label="Status" value="Valid" isBadge />
              <div className="pt-2">
                <TextLink icon={<Download className="h-3 w-3" />}>View Certificate</TextLink>
              </div>
            </CollapsiblePanel>

            {/* TSA Information */}
            <CollapsiblePanel
              title="TSA Information"
              expanded={expandedSections.tsa}
              onToggle={() => toggleSection("tsa")}
            >
              <DetailRow label="TSA Name" value="I-PQSign TSA 01" />
              <DetailRow label="TSA URL" value="https://tsa1.ipqsign.com" isLink />
              <DetailRow label="Policy OID" value="1.3.6.1.4.1.99999.1.1.1" />
              <DetailRow label="Accuracy" value="± 1 second" />
              <DetailRow label="Clock Sync" value="NTP (stratum 1)" />
              <DetailRow label="Status" value="Healthy" isBadge />
              <div className="pt-2">
                <TextLink icon={<ExternalLink className="h-3 w-3" />}>View TSA Details</TextLink>
              </div>
            </CollapsiblePanel>

            {/* Blockchain & IPFS */}
            <CollapsiblePanel
              title="Blockchain & IPFS"
              expanded={expandedSections.blockchain}
              onToggle={() => toggleSection("blockchain")}
            >
              <DetailRow
                label="IPFS CID"
                value="QmX7e5g9J8W2ny42Ks..."
                mono
                copyable
                onCopy={() => copyText("QmX7e5g9J8W2ny42Ks9o8u7G3hST8u1p2L4m6N8o0PqR")}
              />
              <DetailRow label="Blockchain" value="Polygon Amoy" />
              <DetailRow
                label="Transaction Hash"
                value="0x8f7c...a1b2c3d4e5f6"
                mono
                copyable
                onCopy={() => copyText("0x8f7c...a1b2c3d4e5f6")}
              />
              <div className="pt-2">
                <TextLink icon={<ExternalLink className="h-3 w-3" />}>View on Blockchain</TextLink>
              </div>
            </CollapsiblePanel>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatusCard({ icon, title, value, footer, footerColor, iconBg }) {
  return (
    <div className="rounded-lg border border-slate-200/90 bg-white px-2.5 py-1.5 shadow-xs flex items-start gap-2">
      <div className={`p-2 rounded-md shrink-0 ${iconBg}`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-slate-500">{title}</p>
        <p className="text-xs font-bold text-slate-900 truncate mt-0.5">{value}</p>
        {footer && (
          <p className={`text-[10px] mt-0.5 ${footerColor || "text-slate-500"}`}>{footer}</p>
        )}
      </div>
    </div>
  );
}

function VerificationRow({ label, value, action, mono, wrap, onCopy }) {
  return (
    <div className="py-2 flex items-start justify-between gap-2 text-xs">
      <span className="font-semibold text-slate-500 w-32 shrink-0 pt-0.5">{label}</span>

      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {action === "badge" ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 border border-emerald-200">
            <Check className="h-3 w-3" />
            {value}
          </span>
        ) : (
          <span
            className={`text-xs ${
              mono ? "font-mono text-indigo-600" : "text-slate-800 font-medium"
            } ${wrap ? "break-all" : "truncate"}`}
          >
            {value}
          </span>
        )}

        {action === "copy" && (
          <button
            type="button"
            onClick={onCopy}
            className="text-slate-400 hover:text-indigo-600 shrink-0 transition"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}

        {action === "external" && (
          <a href="#" className="text-indigo-600 hover:underline flex items-center gap-1 font-semibold">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}

        {action === "download" && (
          <button type="button" className="text-indigo-600 hover:text-indigo-800">
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TimelineEvent({ event, isLast }) {
  return (
    <div className="relative flex items-start gap-2.5">
      <span className="w-14 shrink-0 pt-0.5 text-[10px] font-semibold text-slate-400 text-right">
        {event.time}
      </span>

      <div className="relative flex flex-col items-center self-stretch">
        <div className="z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-600 ring-2 ring-white">
          <Check className="h-2 w-2 text-white" />
        </div>
        {!isLast && <div className="w-0.5 bg-emerald-500/30 flex-1 my-1" />}
      </div>

      <div
        className={`flex-1 rounded-md border p-2.5 text-xs ${
          event.success
            ? "border-emerald-200 bg-[#f0fbf5] text-slate-800"
            : "border-slate-200/90 bg-white"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-bold text-slate-900 text-xs">{event.title}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{event.description}</p>
          </div>

          {event.tag && (
            <span className="shrink-0 rounded bg-emerald-50 border border-emerald-200 px-2 py-0.5 font-mono text-[10px] font-semibold text-emerald-700">
              {event.tag}
            </span>
          )}
        </div>

        {event.codeBlock && (
          <div className="mt-1.5 rounded border border-emerald-200 bg-emerald-50/50 p-1.5 font-mono text-[10px] text-emerald-800 break-all">
            {event.codeBlock}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, isBadge, isLink, mono, copyable, onCopy }) {
  let valueClass = "text-slate-800 font-semibold";
  if (isLink) {
    valueClass = "text-indigo-600 font-medium hover:underline cursor-pointer";
  } else if (mono) {
    valueClass = "font-mono text-indigo-600";
  }

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 text-xs">
      <span className="text-slate-500 font-medium shrink-0 w-24">{label}</span>
      <div className="flex items-center gap-1.5 flex-1">
        {isBadge ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
            <Check className="h-2.5 w-2.5" />
            {value}
          </span>
        ) : (
          <span className={valueClass}>
            {value}
          </span>
        )}

        {copyable && (
          <button type="button" onClick={onCopy} className="text-slate-400 hover:text-indigo-600">
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function CollapsiblePanel({ title, expanded, onToggle, children }) {
  return (
    <div className="rounded-lg border border-slate-200/90 bg-white p-3.5 shadow-xs">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between font-bold text-slate-900 text-xs pb-1.5 border-b border-slate-100"
      >
        <span>{title}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && <div className="mt-1 divide-y divide-slate-100">{children}</div>}
    </div>
  );
}

function TextLink({ icon, children }) {
  return (
    <a
      href="#"
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition"
    >
      <span>{children}</span>
      {icon}
    </a>
  );
}